import React, { useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import {
    ResponsiveContainer,
    ScatterChart,
    Scatter,
    XAxis,
    YAxis,
    ZAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
    Cell,
    LabelList
} from "recharts";
import { getAnalyticsWidget } from "../../../utils/Requests";
import Logger from "../../../utils/Logger";
import { AnalyticWidgetProps } from "./types";
import { WidgetCard } from "../../../components/analytics";

type ScatterplotDataPoint = {
    brandName: string | null;
    brandId: string | null;
    xValue: number | null;
    yValue: number | null;
    sizeValue: number | null;
};

type ScatterQuadrants = {
    topRight: string;
    topLeft: string;
    bottomRight: string;
    bottomLeft: string;
};

type ScatterAxesInfo = {
    xZeroMeaningful: boolean;
    yZeroMeaningful: boolean;
};

type ScatterInterpretation = {
    title: string;
    description: string;
    insight: string;
    quadrants: ScatterQuadrants | null;
    pairType: string;
    axesInfo: ScatterAxesInfo;
    caveats: string[];
};

type ActiveQuadrant = keyof ScatterQuadrants;

/* eslint-disable camelcase */
type ScatterplotApiDataPoint = {
    brand_name: string | null;
    brand_id: string | null;
    x_value: number | null;
    y_value: number | null;
    size_value: number | null;
};

type ScatterApiQuadrants = {
    top_right: string;
    top_left: string;
    bottom_right: string;
    bottom_left: string;
};

type ScatterApiAxesInfo = {
    x_zero_meaningful: boolean;
    y_zero_meaningful: boolean;
};

type ScatterApiInterpretation = {
    title: string;
    description: string;
    insight: string;
    quadrants: ScatterApiQuadrants | null;
    pair_type: string;
    axes_info: ScatterApiAxesInfo;
    caveats?: string[];
};

type ScatterplotApiResponse = {
    result?: ScatterplotApiDataPoint[];
    interpretation?: ScatterApiInterpretation;
    metric_definitions?: {
        metrics?: Array<{
            key?: string;
            label?: string;
            definition?: string;
        }>;
    };
    widget?: {
        display_name?: string;
        description?: string;
    };
    schema?: Record<string, { description?: string }>;
};
/* eslint-enable camelcase */

type ScatterplotMetricOption = {
    key: string;
    label: string;
};

type ScatterplotMetricDefinition = {
    key: string;
    label: string;
    definition: string;
};

type ScatterplotPreset = {
    id: string;
    label: string;
    xMetric: string;
    yMetric: string;
};

type ScatterplotDataPayload = {
    rows: ScatterplotDataPoint[];
    xMetric: string;
    yMetric: string;
    topN: number;
};

type ScatterplotWidgetProps = AnalyticWidgetProps & {
    onDataLoaded?: (payload: ScatterplotDataPayload) => void;
};

const RESP: any = ResponsiveContainer as any;
const SC: any = ScatterChart as any;
const ST: any = Scatter as any;
const XA: any = XAxis as any;
const YA: any = YAxis as any;
const ZA: any = ZAxis as any;
const CG: any = CartesianGrid as any;
const TT: any = Tooltip as any;
const LG: any = Legend as any;
const RL: any = ReferenceLine as any;
const CL: any = Cell as any;
const LL: any = LabelList as any;

const POINT_COLORS = [
    "#2f80ed", "#e74c3c", "#27ae60", "#f39c12", "#8e44ad",
    "#1abc9c", "#e67e22", "#3498db", "#e91e63", "#00bcd4",
    "#ff5722", "#9c27b0", "#4caf50", "#ff9800", "#607d8b",
    "#795548", "#009688", "#673ab7", "#cddc39", "#03a9f4"
];

const MAX_POINT_LABELS = 15;
const POINT_LABEL_FONT_SIZE = 12;
const POINT_FILL_OPACITY = 0.58;

const ScatterLabel = (props: any): React.ReactElement | null => {
    const { x, y, value, index } = props;
    if (index >= MAX_POINT_LABELS || !value) return null;
    const pointColor = POINT_COLORS[index % POINT_COLORS.length];
    return (
        <text
            x={x}
            y={y - 14}
            textAnchor="middle"
            fontSize={POINT_LABEL_FONT_SIZE}
            fill={pointColor}
            pointerEvents="none"
            fontWeight={600}
        >
            {value}
        </text>
    );
};

const METRIC_OPTIONS: ScatterplotMetricOption[] = [
    { key: "video_starts", label: "Video Starts" },
    { key: "plays", label: "Plays" },
    { key: "users", label: "Users" },
    { key: "views", label: "Views" },
    { key: "hours_watched", label: "Hours Watched" },
    { key: "avg_progress", label: "Avg Progress" },
    { key: "session_conversion", label: "Session Conversion" },
    { key: "bounce_rate", label: "Bounce Rate" },
    { key: "conversion", label: "Conversion" },
    { key: "video_starts_per_user", label: "Video Starts/User" },
    { key: "plays_per_user", label: "Plays/User" },
    { key: "rec_video_starts", label: "Rec Video Starts" },
    { key: "pct_rec_users", label: "% Rec Users" },
    { key: "video_starts_change", label: "Video Starts Change" },
    { key: "plays_change", label: "Plays Change" },
    { key: "users_change", label: "Users Change" },
    { key: "views_change", label: "Views Change" },
    { key: "hours_watched_change", label: "Hours Watched Change" },
    { key: "avg_progress_change", label: "Avg Progress Change" },
    { key: "session_conversion_change", label: "Session Conversion Change" },
    { key: "bounce_rate_change", label: "Bounce Rate Change" },
    { key: "conversion_change", label: "Conversion Change" },
    { key: "video_starts_per_user_change", label: "Video Starts/User Change" },
    { key: "plays_per_user_change", label: "Plays/User Change" },
    { key: "rec_video_starts_change", label: "Rec Video Starts Change" },
    { key: "pct_rec_users_change", label: "% Rec Users Change" }
];

const PRESETS: ScatterplotPreset[] = [
    {
        id: "users_vs_video_starts",
        label: "Users vs Video Starts",
        xMetric: "video_starts",
        yMetric: "users"
    },
    {
        id: "users_change_vs_vs_change",
        label: "Users % Change vs VS % Change",
        xMetric: "video_starts_change",
        yMetric: "users_change"
    },
    {
        id: "rec_vs_total",
        label: "Rec Video Starts vs Video Starts",
        xMetric: "video_starts",
        yMetric: "rec_video_starts"
    },
    {
        id: "conversion_vs_volume",
        label: "Conversion vs Video Starts",
        xMetric: "video_starts",
        yMetric: "conversion"
    },
    {
        id: "progress_vs_hours",
        label: "Avg Progress vs Hours Watched",
        xMetric: "hours_watched",
        yMetric: "avg_progress"
    },
    {
        id: "session_conv_vs_bounce",
        label: "Session Conversion vs Bounce Rate",
        xMetric: "bounce_rate",
        yMetric: "session_conversion"
    }
];

const TOP_N_OPTIONS: number[] = [10, 25, 50, 100];

const DEFAULT_X_METRIC = "video_starts";
const DEFAULT_Y_METRIC = "users";
const DEFAULT_TOP_N = 25;

const prettifyMetricKey = (metricKey: string): string => {
    return metricKey
        .split("_")
        .map(part => (part.length > 0 ? `${part.charAt(0).toUpperCase()}${part.slice(1)}` : part))
        .join(" ");
};

const formatMetricValue = (value: number | null): string => {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return "-";
    }
    if (Number.isInteger(value)) {
        return value.toLocaleString();
    }
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const metricLabelWithUnit = (metricKey: string, labelMap: Record<string, string>): string => {
    const label = labelMap[metricKey] || prettifyMetricKey(metricKey);
    return metricKey.endsWith("_change") ? `${label} (%)` : label;
};

const toFiniteNumber = (value: unknown): number | null => {
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
    }

    if (typeof value === "string" && value.trim().length > 0) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
};

const ScatterTooltip = ({ active, payload, xMetric, yMetric, labelMap, definitionMap, quadrants, activeQuadrant }: any) => {
    if (!active || !payload || payload.length === 0) {
        return null;
    }

    const point = payload[0].payload as ScatterplotDataPoint;
    return (
        <div
            style={{
                backgroundColor: "white",
                border: "1px solid #ccc",
                borderRadius: "4px",
                padding: "10px",
                maxWidth: "300px",
                color: "#1f2937"
            }}
        >
            <p style={{ margin: 0, fontWeight: 700, color: "#111827" }}>{point.brandName || "Unknown"}</p>
            <p style={{ margin: "8px 0 0 0" }}>
                {metricLabelWithUnit(xMetric, labelMap)}: {formatMetricValue(point.xValue)}
            </p>
            <p style={{ margin: "4px 0 0 0" }}>
                {metricLabelWithUnit(yMetric, labelMap)}: {formatMetricValue(point.yValue)}
            </p>
            <p style={{ margin: "4px 0 0 0" }}>
                Video Starts: {formatMetricValue(point.sizeValue)}
            </p>
            {definitionMap?.[xMetric] && (
                <p style={{ margin: "8px 0 0 0", color: "#374151" }}>
                    X: {definitionMap[xMetric]}
                </p>
            )}
            {definitionMap?.[yMetric] && (
                <p style={{ margin: "4px 0 0 0", color: "#374151" }}>
                    Y: {definitionMap[yMetric]}
                </p>
            )}
            {quadrants && activeQuadrant && (
                <p style={{ margin: "8px 0 0 0", color: "#374151", fontWeight: 600 }}>
                    {quadrants[activeQuadrant]}
                </p>
            )}
        </div>
    );
};

const ScatterplotWidget: React.FC<ScatterplotWidgetProps> = ({
    customerConfig,
    filters,
    theme,
    chartOptions: _chartOptions,
    onMetaLoaded,
    onDataLoaded
}) => {
    const [xMetric, setXMetric] = useState(DEFAULT_X_METRIC);
    const [yMetric, setYMetric] = useState(DEFAULT_Y_METRIC);
    const [topN, setTopN] = useState(DEFAULT_TOP_N);
    const [selectedPreset, setSelectedPreset] = useState<string>("custom");

    const [analyticsData, setAnalyticsData] = useState<ScatterplotDataPoint[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [widgetMeta, setWidgetMeta] = useState<any>(null);
    const [interpretation, setInterpretation] = useState<ScatterInterpretation | null>(null);
    const [metricDefinitions, setMetricDefinitions] = useState<ScatterplotMetricDefinition[]>([]);
    const [activeQuadrant, setActiveQuadrant] = useState<ActiveQuadrant | null>(null);

    const metricOptions = useMemo((): ScatterplotMetricOption[] => {
        if (metricDefinitions.length > 0) {
            return metricDefinitions.map(metric => ({ key: metric.key, label: metric.label }));
        }
        return METRIC_OPTIONS;
    }, [metricDefinitions]);

    const metricLabelMap = useMemo((): Record<string, string> => {
        return metricOptions.reduce((acc, option) => {
            acc[option.key] = option.label;
            return acc;
        }, {} as Record<string, string>);
    }, [metricOptions]);

    const metricDefinitionMap = useMemo((): Record<string, string> => {
        return metricDefinitions.reduce((acc, metric) => {
            acc[metric.key] = metric.definition;
            return acc;
        }, {} as Record<string, string>);
    }, [metricDefinitions]);

    const visibleData = useMemo((): Array<ScatterplotDataPoint & { xValue: number; yValue: number }> => {
        return analyticsData.reduce<Array<ScatterplotDataPoint & { xValue: number; yValue: number }>>((acc, point) => {
            const xValue = toFiniteNumber(point.xValue);
            const yValue = toFiniteNumber(point.yValue);

            if (xValue === null || yValue === null) {
                return acc;
            }

            acc.push({
                ...point,
                xValue,
                yValue
            });
            return acc;
        }, []);
    }, [analyticsData]);

    const xDomain = useMemo((): [number, number] => {
        if (visibleData.length === 0) {
            return [0, 0];
        }
        const values = visibleData.map(point => point.xValue);
        return [Math.min(...values), Math.max(...values)];
    }, [visibleData]);

    const yDomain = useMemo((): [number, number] => {
        if (visibleData.length === 0) {
            return [0, 0];
        }
        const values = visibleData.map(point => point.yValue);
        return [Math.min(...values), Math.max(...values)];
    }, [visibleData]);

    useEffect(() => {
        const matchingPreset = PRESETS.find(
            preset => preset.xMetric === xMetric && preset.yMetric === yMetric
        );
        setSelectedPreset(matchingPreset?.id || "custom");
    }, [xMetric, yMetric]);

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            setIsLoading(true);
            setError(null);

            try {
                const extraParams: Record<string, string | number> = {};
                extraParams["x_metric"] = xMetric;
                extraParams["y_metric"] = yMetric;
                extraParams["top_n"] = topN;

                const response = await getAnalyticsWidget(
                    customerConfig,
                    "scatterplot",
                    filters.startDate,
                    filters.endDate,
                    filters.attributionId,
                    filters.brandName,
                    filters.devicePlatform,
                    undefined,
                    undefined,
                    extraParams
                );

                const scatterResponse = response as unknown as ScatterplotApiResponse;

                const apiMetricDefinitions = Array.isArray(scatterResponse?.metric_definitions?.metrics)
                    ? (scatterResponse.metric_definitions?.metrics ?? [])
                    : [];
                const mappedMetricDefinitions = apiMetricDefinitions
                    .filter(metric => typeof metric?.key === "string" && metric.key.trim().length > 0)
                    .map(metric => {
                        const key = String(metric.key);
                        return {
                            key,
                            label: typeof metric.label === "string" && metric.label.trim().length > 0
                                ? metric.label
                                : prettifyMetricKey(key),
                            definition: typeof metric.definition === "string" ? metric.definition : ""
                        };
                    });
                setMetricDefinitions(mappedMetricDefinitions);

                const data = scatterResponse?.result;
                const dataArray = Array.isArray(data) ? (data as ScatterplotApiDataPoint[]) : [];
                const mappedRows = dataArray.map(row => ({
                        brandName: row.brand_name,
                        brandId: row.brand_id,
                        xValue: row.x_value,
                        yValue: row.y_value,
                        sizeValue: row.size_value
                    }));
                setAnalyticsData(mappedRows);
                if (onDataLoaded) {
                    onDataLoaded({
                        rows: mappedRows,
                        xMetric,
                        yMetric,
                        topN
                    });
                }

                const apiInterpretation = scatterResponse?.interpretation;
                setInterpretation(apiInterpretation ? {
                    title: apiInterpretation.title,
                    description: apiInterpretation.description,
                    insight: apiInterpretation.insight,
                    pairType: apiInterpretation.pair_type,
                    axesInfo: {
                        xZeroMeaningful: apiInterpretation.axes_info.x_zero_meaningful,
                        yZeroMeaningful: apiInterpretation.axes_info.y_zero_meaningful
                    },
                    quadrants: apiInterpretation.quadrants ? {
                        topRight: apiInterpretation.quadrants.top_right,
                        topLeft: apiInterpretation.quadrants.top_left,
                        bottomRight: apiInterpretation.quadrants.bottom_right,
                        bottomLeft: apiInterpretation.quadrants.bottom_left
                    } : null,
                    caveats: Array.isArray(apiInterpretation.caveats) ? apiInterpretation.caveats : []
                } : null);
                setActiveQuadrant(null);

                const meta = scatterResponse?.widget;
                if (meta) {
                    setWidgetMeta(meta);
                    if (onMetaLoaded) {
                        const apiSchemaForDocs = scatterResponse?.schema
                            ? Object.fromEntries(
                                Object.entries(scatterResponse.schema).map(([key, value]) => [
                                    key,
                                    { description: value?.description || "" }
                                ])
                            )
                            : {};

                        const metricDefinitionsSchemaForDocs = mappedMetricDefinitions.length > 0
                            ? Object.fromEntries(
                                mappedMetricDefinitions.map(metric => [
                                    `metric_definitions.${metric.key}`,
                                    {
                                        description: metric.definition || `${metric.label}: definition not provided.`
                                    }
                                ])
                            )
                            : {};

                        const schemaForDocs = Object.keys({ ...apiSchemaForDocs, ...metricDefinitionsSchemaForDocs }).length > 0
                            ? { ...apiSchemaForDocs, ...metricDefinitionsSchemaForDocs }
                            : undefined;

                        onMetaLoaded({
                            widgetKey: "scatterplot",
                            displayName: meta.display_name || "Scatterplot",
                            description: meta.description || "",
                            schema: schemaForDocs
                        });
                    }
                }
            } catch (err) {
                Logger.error("Error fetching scatterplot data", err);
                setError("Failed to fetch scatterplot data");
                setInterpretation(null);
                setActiveQuadrant(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [customerConfig, filters, xMetric, yMetric, topN]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePresetChange = (presetId: string): void => {
        setSelectedPreset(presetId);
        if (presetId === "custom") {
            return;
        }

        const preset = PRESETS.find(item => item.id === presetId);
        if (!preset) {
            return;
        }

        setXMetric(preset.xMetric);
        setYMetric(preset.yMetric);
    };

    const showZeroXAxis = interpretation?.axesInfo?.xZeroMeaningful ?? xMetric.endsWith("_change");
    const showZeroYAxis = interpretation?.axesInfo?.yZeroMeaningful ?? yMetric.endsWith("_change");

    const xBaseline = showZeroXAxis ? 0 : (xDomain[0] + xDomain[1]) / 2;
    const yBaseline = showZeroYAxis ? 0 : (yDomain[0] + yDomain[1]) / 2;

    const handlePointHover = (event: any): void => {
        if (!interpretation?.quadrants) {
            setActiveQuadrant(prev => (prev === null ? prev : null));
            return;
        }

        const point = event?.payload as ScatterplotDataPoint | undefined;
        if (!point || point.xValue === null || point.yValue === null) {
            setActiveQuadrant(prev => (prev === null ? prev : null));
            return;
        }

        const horizontal = point.xValue >= xBaseline ? "Right" : "Left";
        const vertical = point.yValue >= yBaseline ? "top" : "bottom";
        const nextQuadrant = `${vertical}${horizontal}` as ActiveQuadrant;
        setActiveQuadrant(prev => (prev === nextQuadrant ? prev : nextQuadrant));
    };

    return (
        <>
            <div
                style={{
                    display: "grid",
                    gap: "12px",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    marginBottom: "16px"
                }}
            >
                <Form.Group>
                    <Form.Label>Preset</Form.Label>
                    <Form.Control
                        as="select"
                        value={selectedPreset}
                        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => handlePresetChange(event.target.value)}
                    >
                        <option value="custom">Custom</option>
                        {PRESETS.map(preset => (
                            <option key={preset.id} value={preset.id}>
                                {preset.label}
                            </option>
                        ))}
                    </Form.Control>
                </Form.Group>

                <Form.Group>
                    <Form.Label>X Axis</Form.Label>
                    <Form.Control
                        as="select"
                        value={xMetric}
                        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setXMetric(event.target.value)}
                    >
                        {metricOptions.map(option => (
                            <option key={option.key} value={option.key}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Control>
                </Form.Group>

                <Form.Group>
                    <Form.Label>Y Axis</Form.Label>
                    <Form.Control
                        as="select"
                        value={yMetric}
                        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setYMetric(event.target.value)}
                    >
                        {metricOptions.map(option => (
                            <option key={option.key} value={option.key}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Control>
                </Form.Group>

                <Form.Group>
                    <Form.Label>Top N</Form.Label>
                    <Form.Control
                        as="select"
                        value={String(topN)}
                        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                            const parsed = parseInt(event.target.value, 10);
                            setTopN(Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_TOP_N);
                        }}
                    >
                        {TOP_N_OPTIONS.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </Form.Control>
                </Form.Group>
            </div>

            <WidgetCard
                title={widgetMeta?.display_name || "Scatterplot"}
                description={widgetMeta?.description}
                isLoading={isLoading}
                error={error}
                theme={theme}
            >
            <div
                style={{
                    backgroundColor: theme.chartBg,
                    padding: "16px 16px 6px 16px",
                    borderRadius: "5px",
                    marginBottom: "2px",
                    width: "95%",
                    marginLeft: "auto",
                    marginRight: "auto"
                }}
            >
                <RESP width="100%" height={720}>
                    <SC margin={{ top: 52, right: 52, left: 72, bottom: 20 }}>
                        <CG stroke="rgba(120, 120, 120, 0.22)" strokeDasharray="3 3" />
                        <XA
                            type="number"
                            dataKey="xValue"
                            name={metricLabelWithUnit(xMetric, metricLabelMap)}
                            tickFormatter={(value: number) => formatMetricValue(value)}
                            label={{ value: metricLabelWithUnit(xMetric, metricLabelMap), position: "insideBottom", offset: -8, style: { fill: "#666", fontSize: 14 } }}
                            tick={{ fontSize: 12 }}
                        />
                        <YA
                            type="number"
                            dataKey="yValue"
                            name={metricLabelWithUnit(yMetric, metricLabelMap)}
                            tickFormatter={(value: number) => formatMetricValue(value)}
                            label={{ value: metricLabelWithUnit(yMetric, metricLabelMap), angle: -90, position: "insideLeft", offset: 12, style: { fill: "#666", fontSize: 14 } }}
                            tick={{ fontSize: 12 }}
                        />
                        <ZA type="number" dataKey="sizeValue" range={[140, 780]} name="Video Starts" />
                        {showZeroXAxis && <RL x={0} stroke="#a3a3a3" strokeDasharray="4 4" />}
                        {showZeroYAxis && <RL y={0} stroke="#a3a3a3" strokeDasharray="4 4" />}
                        <TT
                            content={(
                                <ScatterTooltip
                                    xMetric={xMetric}
                                    yMetric={yMetric}
                                    labelMap={metricLabelMap}
                                    definitionMap={metricDefinitionMap}
                                    quadrants={interpretation?.quadrants}
                                    activeQuadrant={activeQuadrant}
                                />
                            )}
                        />
                        <LG align="right" verticalAlign="bottom" wrapperStyle={{ paddingTop: "2px", right: 0 }} />
                        <ST
                            name="Video Starts Count"
                            data={visibleData}
                            fill="#2f80ed"
                            isAnimationActive={false}
                            onMouseMove={handlePointHover}
                            onMouseLeave={() => setActiveQuadrant(prev => (prev === null ? prev : null))}
                        >
                            {visibleData.map((_entry, index) => (
                                <CL key={`cell-${index}`} fill={POINT_COLORS[index % POINT_COLORS.length]} fillOpacity={POINT_FILL_OPACITY} />
                            ))}
                            <LL dataKey="brandName" content={ScatterLabel} />
                        </ST>
                    </SC>
                </RESP>
            </div>

            {!isLoading && !error && visibleData.length === 0 && (
                <p style={{ color: "#666", margin: "2px auto 4px auto", width: "95%" }}>
                    No brands to plot for this metric pair and date range.
                </p>
            )}

            {visibleData.length < analyticsData.length && (
                <p style={{ color: "#666", margin: "2px auto 4px auto", width: "95%" }}>
                    Hidden points: {analyticsData.length - visibleData.length} due to null axis values.
                </p>
            )}

            {interpretation && (
                <div
                    style={{
                        backgroundColor: theme.chartBg,
                        border: `1px solid ${theme.border}`,
                        borderRadius: "6px",
                        padding: "14px",
                        marginBottom: "20px",
                        marginTop: "0",
                        width: "75%",
                        marginLeft: "auto",
                        marginRight: "auto"
                    }}
                >
                    <h5 style={{ margin: "0 0 6px 0", color: theme.text }}>
                        {interpretation.title}
                    </h5>
                    <p style={{ margin: "0 0 8px 0", color: theme.text, fontWeight: 600 }}>
                        {interpretation.insight}
                    </p>
                    {interpretation.description && (
                        <p style={{ margin: "0 0 10px 0", color: "#666" }}>
                            {interpretation.description}
                        </p>
                    )}

                    {Array.isArray(interpretation.caveats) && interpretation.caveats.length > 0 && (
                        <ul style={{ margin: 0, paddingLeft: "18px", color: "#666" }}>
                            {interpretation.caveats.map((caveat, index) => (
                                <li key={`${caveat}-${index}`}>{caveat}</li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            <div
                style={{
                    backgroundColor: theme.chartBg,
                    padding: "15px",
                    borderRadius: "5px",
                    marginBottom: "20px",
                    overflowX: "auto"
                }}
            >
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr style={{ borderBottom: "2px solid #ddd" }}>
                            <th style={{ padding: "10px", textAlign: "left" }}>Brand</th>
                            <th style={{ padding: "10px", textAlign: "left" }}>Brand ID</th>
                            <th style={{ padding: "10px", textAlign: "right" }}>{metricLabelWithUnit(xMetric, metricLabelMap)}</th>
                            <th style={{ padding: "10px", textAlign: "right" }}>{metricLabelWithUnit(yMetric, metricLabelMap)}</th>
                            <th style={{ padding: "10px", textAlign: "right" }}>Video Starts</th>
                        </tr>
                    </thead>
                    <tbody>
                        {analyticsData.map((row, idx) => (
                            <tr key={`${row.brandId || row.brandName || "unknown"}-${idx}`} style={{ borderBottom: "1px solid #eee" }}>
                                <td style={{ padding: "10px" }}>{row.brandName || "Unknown"}</td>
                                <td style={{ padding: "10px" }}>{row.brandId || "-"}</td>
                                <td style={{ padding: "10px", textAlign: "right" }}>{formatMetricValue(row.xValue)}</td>
                                <td style={{ padding: "10px", textAlign: "right" }}>{formatMetricValue(row.yValue)}</td>
                                <td style={{ padding: "10px", textAlign: "right" }}>{formatMetricValue(row.sizeValue)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            </WidgetCard>
        </>
    );
};

export default ScatterplotWidget;